import type { CustomFieldType } from '@molt/shared';
import type { LocaleFormattingTranslate } from './locale-formatting';

/**
 * FE-80.1 — the five custom-field type nouns, in ONE place.
 *
 * ⚠ This leaf exists because the same five English words had THREE independent
 * authors. `card-back/CardCustomFieldsSection.tsx` routed its own copy under
 * `cardBack.customFields.type.*`; `board/BoardCustomFieldsSection.tsx` carried a
 * byte-identical unrouted `TYPE_LABELS` record; the card-custom-fields slice
 * measured the pair, declined to share across the frame boundary, and wrote the
 * rule that settled it: *"Two independent authors of one English word is a
 * finding; a third is the argument for extracting a leaf and a
 * `primitives.customFieldType.*` namespace."* The board-frame slice is the
 * third author, so the extraction is taken here rather than argued again.
 *
 * `primitives.*` rather than either frame's namespace, on the established reach
 * rule: the nouns name a wire enum (`CUSTOM_FIELD_TYPES`), not a surface, and
 * both frames render the same five values for the same five wire members. The
 * five `cardBack.customFields.type.*` keys were REMOVED in the same commit —
 * they were web-fallback-only (`packages/shared`'s catalogue carries activity
 * and notification templates, never web chrome), so no served translation could
 * be orphaned by the rename.
 *
 * The `t` parameter is positional and first, matching `dueStatusLabel` and
 * `formatCardDate` (`lib/card-dates.ts`) rather than the options-object shape
 * `cardAccessibleDescription` uses — those are the functions this one sits
 * beside in a call chain.
 */
export function customFieldTypeLabel(
  t: LocaleFormattingTranslate,
  type: CustomFieldType,
): string {
  switch (type) {
    case 'text':
      return t('primitives.customFieldType.text');
    case 'number':
      return t('primitives.customFieldType.number');
    case 'checkbox':
      return t('primitives.customFieldType.checkbox');
    case 'date':
      return t('primitives.customFieldType.date');
    case 'dropdown':
      return t('primitives.customFieldType.dropdown');
  }
}
